import React, { useReducer, useEffect, useRef } from 'react';
import cn from 'classnames';
import styles from './WinMessages.scss';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EDebugWinPresentationEvents } from '@Core/Win/States/WinPresentationStates';
import { IWinPresentationStep } from '@Core/Win/WinPresentation.types';

interface IComponentState {
    performanceStamp: number;
    timeStampGeneral: number;
    timeDeclared: number;
    winData: Record<string, { targetValue: number; duration: number; timeStamp?: number; delay?: number | null; waitForOdometer?: boolean }>;
}

const WinMessagesTimer: React.FC = () => {
    const [state, setState] = useReducer((s: IComponentState, ns: Partial<IComponentState>) => ({ ...s, ...ns }), {
        performanceStamp: 0,
        timeStampGeneral: 0,
        timeDeclared: 0,
        winData: {}
    });

    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        const onWinPresentationStartProcessed = () => {
            setState({
                performanceStamp: Date.now(),
                timeStampGeneral: 0,
                timeDeclared: 0,
                winData: {}
            });
        };

        const onChange = (step: IWinPresentationStep & { delay?: number; waitForOdometer?: boolean }) => {
            const timeStamp = (Date.now() - stateRef.current.performanceStamp) / 1000;
            const winData = { ...stateRef.current.winData };

            winData[step.state] = {
                targetValue: step.odometer?.tween?.targetValue ?? 0,
                duration: step.timer?.duration ?? step.odometer?.tween?.duration ?? 0,
                timeStamp,
                delay: step.delay ? Math.round(step.delay * 100) / 100 : null,
                waitForOdometer: step.waitForOdometer
            };

            const totalDeclared = Object.values(winData).reduce((sum, d) => sum + (d.duration ?? 0), 0);

            setState({
                winData,
                timeStampGeneral: timeStamp,
                timeDeclared: totalDeclared
            });
        };

        EventsCenter.on(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_START_PROCESSED, onWinPresentationStartProcessed);
        EventsCenter.on(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_CHANGE, onChange);

        return () => {
            EventsCenter.off(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_START_PROCESSED, onWinPresentationStartProcessed);
            EventsCenter.off(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_CHANGE, onChange);
        };
    }, []);

    return (
        <div className={styles.winMessages_timer_container}>
            <div className={styles.winMessages_header}>Win Message Events Time's</div>

            {Object.keys(state.winData).length > 0 ? (
                <>
                    <div className={styles.winMessages_timer_table}>
                        <div className={cn(styles.winMessages_timer_table_row, styles.winMessages_timer_table_header)}>
                            <div>Key</div>
                            <div>Duration</div>
                            <div>Target Value</div>
                            <div>Time Stamp</div>
                        </div>

                        {Object.entries(state.winData).map(([key, data]) => (
                            <div key={key} className={styles.winMessages_timer_table_row}>
                                <div className={styles.truncateRight}>{key}</div>
                                <div>{data.duration === 0 ? '' : `${data.duration} ms / ${(data.duration * 0.001).toFixed(3)} s`}</div>
                                <div>{data.targetValue === 0 ? '' : data.targetValue}</div>
                                <div>{data.timeStamp && data.timeStamp !== 0 && <>{data.timeStamp.toFixed(3)} s</>}</div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.winMessages_timer_summary}>
                        Total declared: {state.timeDeclared} ms / {(state.timeDeclared * 0.001).toFixed(3)} s, Total time: {state.timeStampGeneral.toFixed(3)} s
                    </div>
                </>
            ) : (
                <>No Win event registered</>
            )}
        </div>
    );
};

export default WinMessagesTimer;
